import { useRef, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import RecordManager from "@/components/RecordManager";
import { EXAM_TYPES } from "@/lib/constants";
import { ListChecks, Loader2, X } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";

export default function GeneralExams({ studentId, readOnly }) {
  const [resOpen, setResOpen] = useState(null);
  const [resSaving, setResSaving] = useState(false);
  const loadFn = useRef(null);

  const openResultEntry = async () => {
    try {
      const { data } = await api.get(`/general-exams/${studentId}`);
      const rows = data.filter((r) => !Number(r.total_net)).map((r) => ({ ...r, tn: "", rk: "" }));
      if (!rows.length) return toast.info("Sonuç bekleyen deneme yok");
      setResOpen(rows);
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const saveResults = async () => {
    const updates = resOpen
      .filter((r) => r.tn !== "" || r.rk !== "")
      .map((r) => ({ id: r.id, total_net: Number(r.tn || 0), ranking: Number(r.rk || 0) }));
    if (!updates.length) return toast.error("En az bir satır doldurun");
    setResSaving(true);
    try {
      const { data } = await api.put(`/records-bulk/general-exams/${studentId}`, { updates });
      toast.success(`${data.updated} deneme sonucu kaydedildi`);
      setResOpen(null);
      loadFn.current?.();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setResSaving(false);
    }
  };

  const chart = (items) => {
    const sorted = [...items].sort((a, b) => (a.date || "").localeCompare(b.date || ""));
    const map = {};
    sorted.forEach((i) => {
      const d = i.date || "-";
      map[d] = map[d] || { date: d };
      map[d][i.exam_type] = Number(i.total_net || 0);
    });
    const data = Object.values(map);
    if (data.length === 0) return null;
    return (
      <div className="card-ta p-4">
        <div className="text-sm text-slate-500 mb-3">Genel Deneme Net Karşılaştırması (TYT / AYT)</div>
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="date" stroke="#94A3B8" fontSize={11} />
            <YAxis stroke="#94A3B8" fontSize={12} />
            <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 8 }} />
            <Legend />
            <Line type="monotone" dataKey="TYT" stroke="#3B82F6" strokeWidth={2} dot={{ r: 3 }} />
            <Line type="monotone" dataKey="AYT" stroke="#F59E0B" strokeWidth={2} dot={{ r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  };
  return (
    <>
      <RecordManager
      studentId={studentId}
      readOnly={readOnly}
      kind="general-exams"
      title="Genel Deneme Sonuç Tablosu"
      description="TYT / AYT genel denemeleri, toplam net ve sıralama karşılaştırması."
      renderExtra={chart}
      renderActions={(reload) => {
        loadFn.current = reload;
        return (
          <Button onClick={openResultEntry} className="bg-emerald-600 hover:bg-emerald-700" data-testid="general-results-btn">
            <ListChecks size={16} /><span className="ml-1">Toplu Sonuç Gir</span>
          </Button>
        );
      }}
      fields={[
        { name: "date", label: "Tarih", type: "date", required: true },
        { name: "exam_type", label: "Sınav", type: "select", options: EXAM_TYPES, required: true },
        { name: "name", label: "Deneme Adı", type: "text", wide: true },
        { name: "publisher", label: "Yayınevi", type: "text" },
        { name: "total_net", label: "Toplam Net", type: "number", required: true },
        { name: "ranking", label: "Sıralama", type: "number" },
      ]}
      columns={[
        { name: "date", label: "Tarih" },
        {
          name: "exam_type",
          label: "Sınav",
          render: (r) => (
            <span
              className={`text-xs px-2 py-0.5 rounded-full border ${
                r.exam_type === "TYT"
                  ? "bg-blue-100 text-blue-700 border-blue-200"
                  : "bg-amber-100 text-amber-700 border-amber-200"
              }`}
            >
              {r.exam_type}
            </span>
          ),
        },
        { name: "name", label: "Deneme" },
        { name: "publisher", label: "Yayınevi", render: (r) => (r.publisher ? <span className="text-xs font-semibold text-indigo-600">{r.publisher}</span> : "-") },
        { name: "total_net", label: "Net", render: (r) => <span className="font-mono font-semibold text-emerald-600">{Number(r.total_net || 0).toFixed(2)}</span> },
        { name: "ranking", label: "Sıralama", render: (r) => (r.ranking ? Number(r.ranking).toLocaleString("tr-TR") : "-") },
      ]}
    />

      {resOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setResOpen(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()} data-testid="general-results-modal">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-lg font-bold flex items-center gap-2"><ListChecks size={18} className="text-emerald-600" /> Toplu Sonuç Gir (elle)</h3>
              <button onClick={() => setResOpen(null)} className="text-slate-400 hover:text-slate-700" data-testid="general-results-close"><X size={18} /></button>
            </div>
            <p className="text-xs text-slate-500 mb-3">Sonuç bekleyen denemeler listelenir. Toplam net ve sıralama girilir; tüm panellerde görünür.</p>
            <div className="space-y-2">
              <div className="grid grid-cols-[1fr_96px_96px] gap-2 text-[11px] font-bold text-slate-400 uppercase px-1">
                <span>Deneme</span><span className="text-center">Toplam Net</span><span className="text-center">Sıralama</span>
              </div>
              {resOpen.map((r) => (
                <div key={r.id} className="grid grid-cols-[1fr_96px_96px] gap-2 items-center bg-slate-50 rounded-lg px-2 py-1.5">
                  <div className="text-xs font-semibold text-slate-700 truncate">
                    {r.exam_type ? <span className="text-indigo-600">{r.exam_type} · </span> : null}{r.name || "Deneme"}
                    <span className="text-slate-400 font-normal"> · {r.date || "-"}</span>
                  </div>
                  <input type="number" step="0.01" className="ta-input !py-1 !text-xs text-center border-indigo-200" value={r.tn} onChange={(e) => setResOpen(resOpen.map((x) => (x.id === r.id ? { ...x, tn: e.target.value } : x)))} data-testid={`res-tn-${r.id}`} />
                  <input type="number" className="ta-input !py-1 !text-xs text-center" value={r.rk} onChange={(e) => setResOpen(resOpen.map((x) => (x.id === r.id ? { ...x, rk: e.target.value } : x)))} data-testid={`res-rk-${r.id}`} />
                </div>
              ))}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setResOpen(null)}>Vazgeç</Button>
              <Button onClick={saveResults} disabled={resSaving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="general-results-save">
                {resSaving ? <Loader2 className="animate-spin" size={15} /> : <ListChecks size={15} />}<span className="ml-1">Sonuçları Kaydet</span>
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
